import React, { useState, useRef, useEffect, useCallback } from 'react';
import '../../assets/css/Modals/imageEditor.scss';
import "../../config";
import api from '../../utils/api';
import ColorPicker from './ColorPicker';

const ImageEditor = ({ selectedImage, onClose, onDetectRect }) => {

    const [colors, setColors] = useState({
        blue: '#0366d6', // Blue-500
        green: '#22c55e',  // Green-500
        red: '#ff6b6b',  // Red-500
        yellow: '#ffcc00'  // Yellow-500
    });

    const [showColorPicker, setShowColorPicker] = useState(false);

    const iconRotateSvgPath = 'M-.0975-12.3004c5.789 0 10.6252 4.1546 11.6855 9.6386h3.2079l-5 8.72-5-8.72h2.9367c-.9838-3.397-4.1205-5.8886-7.8303-5.8886-4.4953 0-8.1525 3.6572-8.1525 8.1525s3.6572 8.1525 8.1525 8.1525c1.2107 0 2.3751-.2585 3.461-.7685l1.5942 3.3942c-1.5884.746-3.2891 1.1242-5.0551 1.1242C-6.6605 11.5045-12 6.165-12-.398S-6.6605-12.3004-.0975-12.3004z';

    // Helper function to safely get base64 from image, handling CORS issues
    const getImageBase64 = useCallback(async (image) => {
        // console.log('Getting base64 for image from:', image.src);

        try {
            // Try direct canvas method first
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = image.width;
            canvas.height = image.height;
            ctx.drawImage(image, 0, 0);

            // Test if canvas is tainted by trying to get image data
            const base64Data = canvas.toDataURL('image/png').split(',')[1];
            // console.log('Direct canvas method successful');
            return base64Data;
        } catch (corsError) {
            console.warn('Canvas is tainted due to CORS, fetching image as blob...', corsError);

            try {
                // Fallback: fetch the image as blob and convert to base64
                const response = await fetch(image.src);
                if (!response.ok) {
                    throw new Error(`Failed to fetch image: ${response.statusText}`);
                }

                const blob = await response.blob();
                console.log('Successfully fetched image as blob, converting to base64...');

                return new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => {
                        const base64 = reader.result.split(',')[1]; // Remove data:image/jpeg;base64, prefix
                        console.log('Blob to base64 conversion successful');
                        resolve(base64);
                    };
                    reader.onerror = (error) => {
                        console.error('FileReader error:', error);
                        reject(error);
                    };
                    reader.readAsDataURL(blob);
                });
            } catch (fetchError) {
                console.error('Failed to fetch image as blob:', fetchError);
                throw new Error('Unable to get image data due to CORS restrictions');
            }
        }
    }, []);

    // Helper function to safely get base64 from canvas, handling CORS issues
    const getCanvasBase64Safe = useCallback((canvas, quality = 0.95) => {
        try {
            const result = canvas.toDataURL('image/jpeg', quality);
            console.log('Canvas toDataURL successful');
            return result;
        } catch (corsError) {
            console.warn('Canvas toDataURL failed, likely due to CORS:', corsError);
            // Return a placeholder or handle the error as appropriate
            throw new Error('Cannot export canvas data due to CORS restrictions');
        }
    }, []);

    // Function to draw the rotate icon at a specific position
    const drawIconRotate = useCallback((ctx, x, y, size = 20) => {
        ctx.save();

        // Set up the icon styling
        ctx.fillStyle = colors.green;
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1;

        // Translate to the position where we want to draw the icon
        ctx.translate(x, y);

        // Scale the icon to the desired size
        const scale = size / 24; // Original icon is roughly 24x24
        ctx.scale(scale, scale);

        // Create a new Path2D from the SVG path
        const iconPath = new Path2D(iconRotateSvgPath);

        // Draw the icon with fill and stroke
        ctx.fill(iconPath);
        ctx.stroke(iconPath);

        ctx.restore();
    }, [colors.green]);

    const [scale, setScale] = useState(0.5); // Fixed scale value
    const [defaultScaleForReset, setDefaultScaleForReset] = useState(0.5); // Default scale for reset
    const [rotation, setRotation] = useState(0); // rotation in degrees for the image
    const canvasRef = useRef(null);
    const previewCanvasRef = useRef(null); // Ref for preview canvas
    const imageContainerRef = useRef(null);
    const prevScaleRef = useRef(0); // Track previous scale for coordinate updates
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageObj, setImageObj] = useState(null);
    // Rectangle detection and manipulation states
    const [rectPoints, setRectPoints] = useState(null);
    const [originalRectPoints, setOriginalRectPoints] = useState(null); // Store unrotated rectangle points
    const [isDraggingPoint, setIsDraggingPoint] = useState(false);
    const [activePointIndex, setActivePointIndex] = useState(null);
    const [arrowPoint, setArrowPoint] = useState(null);
    const [rectRotation, setRectRotation] = useState(0); // rotation in degrees for the rectangle
    const [isRotating, setIsRotating] = useState(false);
    const [centerPoint, setCenterPoint] = useState(null);
    const [showPreview, setShowPreview] = useState(false); // State to toggle preview view
    const [isProcessingPreview, setIsProcessingPreview] = useState(false); // State to track preview processing
    const [previewCompleted, setPreviewCompleted] = useState(false); // State to track if preview has been completed
    const [rectDetected, setRectDetected] = useState(false); // State to track if rectangle is detected
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 }); // Track mouse position for rotate icon
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 }); // Track offset when dragging rectangle
    const [standardizedImageId, setStandardizedImageId] = useState(null); // Store new standardized image ID for existing images
    const [urlNewStandardizedImage, setUrlNewStandardizedImage] = useState(null); // Store URL of the new standardized image for preview

    // Handle scale input change
    const handleScaleRatioChange = (e) => {
        const inputValue = e.target.value;
        const newScale = Number(inputValue);

        // Validate the scale (allow reasonable range)
        if (isNaN(newScale) || newScale <= 0) return;

        // Clamp scale to reasonable bounds
        const clampedScale = Math.max(0.1, Math.min(newScale, 2.0));
        setScale(clampedScale);
    };

    // Handle increase scale button click
    const handleIncreaseScale = useCallback(() => {
        const newScale = Math.min(scale + 0.01, 2.0); // Max 2.0
        setScale(newScale);
    }, [scale]);

    // Handle decrease scale button click
    const handleDecreaseScale = useCallback(() => {
        const newScale = Math.max(scale - 0.01, 0.1); // Min 0.1
        setScale(newScale);
    }, [scale]);

    // Handle mouse wheel for scaling
    const handleWheel = useCallback((e) => {
        e.preventDefault(); // Prevent page scroll

        // Use same increments as increase/decrease buttons
        if (e.deltaY > 0) {
            // Wheel down = zoom out (same as decrease button)
            handleDecreaseScale();
        } else {
            // Wheel up = zoom in (same as increase button)
            handleIncreaseScale();
        }
    }, [handleIncreaseScale, handleDecreaseScale]);

    // Handle detect rectangle
    const handleDetectRect = () => {

        // Create default rectangle based on the image dimensions
        if (canvasRef.current && imageObj) {
            const container = imageContainerRef.current;
            const containerWidth = container.clientWidth;
            const containerHeight = container.clientHeight;

            // Use fixed rectangle dimensions
            const rectWidth = 200;
            const rectHeight = 300;

            const centerX = containerWidth / 2;
            const centerY = containerHeight / 2;

            const halfWidth = rectWidth / 2;
            const halfHeight = rectHeight / 2;

            // Create 8 points around the rectangle - these are the UNROTATED points (at 0 degrees)
            const points = {
                TopLeft: { x: centerX - halfWidth, y: centerY - halfHeight },
                CenterTop: { x: centerX, y: centerY - halfHeight },
                TopRight: { x: centerX + halfWidth, y: centerY - halfHeight },
                CenterRight: { x: centerX + halfWidth, y: centerY },
                BottomRight: { x: centerX + halfWidth, y: centerY + halfHeight },
                CenterBottom: { x: centerX, y: centerY + halfHeight },
                BottomLeft: { x: centerX - halfWidth, y: centerY + halfHeight },
                CenterLeft: { x: centerX - halfWidth, y: centerY }
            };

            // Calculate arrow point at the top center (arrow will always point up)
            const arrowLength = 60; // Fixed arrow length
            const arrowPoint = {
                x: centerX,
                y: centerY - arrowLength
            };

            // Save the original unrotated points
            setOriginalRectPoints({ ...points });

            // If there's already a rectangle rotation, apply it to get the displayed points
            let displayedPoints = { ...points };
            if (rectRotation !== 0) {
                const center = { x: centerX, y: centerY };
                displayedPoints = {
                    TopLeft: rotatePoint(points.TopLeft, center, rectRotation),
                    CenterTop: rotatePoint(points.CenterTop, center, rectRotation),
                    TopRight: rotatePoint(points.TopRight, center, rectRotation),
                    CenterRight: rotatePoint(points.CenterRight, center, rectRotation),
                    BottomRight: rotatePoint(points.BottomRight, center, rectRotation),
                    CenterBottom: rotatePoint(points.CenterBottom, center, rectRotation),
                    BottomLeft: rotatePoint(points.BottomLeft, center, rectRotation),
                    CenterLeft: rotatePoint(points.CenterLeft, center, rectRotation)
                };
            }

            setRectPoints(displayedPoints);
            setArrowPoint(arrowPoint);
            setCenterPoint({ x: centerX, y: centerY });

            // Draw the rectangle and points
            drawImageWithRect(imageObj, displayedPoints, arrowPoint);

            // Set rectangle as detected to show Preview and Confirm buttons
            setRectDetected(true);
            // Hide preview if it was active
            setShowPreview(false);
            setPreviewCompleted(false); // Reset preview completion when rectangle changes
        }
    };

    // Function to render the preview image (fallback when API fails)
    const renderPreview = useCallback(() => {
        if (!rectPoints || !imageObj || !centerPoint || !originalRectPoints) return;

        const container = imageContainerRef.current;
        const previewCanvas = previewCanvasRef.current;
        if (!container || !previewCanvas) return;

        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;

        // Get rectangle dimensions from original unrotated points
        const rectWidth = Math.abs(originalRectPoints.TopRight.x - originalRectPoints.TopLeft.x);
        const rectHeight = Math.abs(originalRectPoints.BottomLeft.y - originalRectPoints.TopLeft.y);

        // Calculate how the original image is displayed on the canvas
        const imgRatio = imageObj.width / imageObj.height;
        let displayHeight = containerHeight * 0.9;
        let displayWidth = displayHeight * imgRatio;
        displayWidth *= scale;
        displayHeight *= scale;

        // Calculate the mapping from canvas coordinates to image coordinates
        const canvasToImageScaleX = imageObj.width / displayWidth;
        const canvasToImageScaleY = imageObj.height / displayHeight;

        // Calculate the image center on canvas
        const imageCenterX = containerWidth / 2;
        const imageCenterY = containerHeight / 2;

        // Convert rectangle center coordinates to image coordinates
        const rectCenterImageX = (centerPoint.x - imageCenterX) * canvasToImageScaleX + imageObj.width / 2;
        const rectCenterImageY = (centerPoint.y - imageCenterY) * canvasToImageScaleY + imageObj.height / 2;
        const rectWidthImage = rectWidth * canvasToImageScaleX;
        const rectHeightImage = rectHeight * canvasToImageScaleY;

        // Clamp rectangle bounds to image boundaries
        const sourceX = Math.max(0, Math.min(imageObj.width - rectWidthImage, rectCenterImageX - rectWidthImage / 2));
        const sourceY = Math.max(0, Math.min(imageObj.height - rectHeightImage, rectCenterImageY - rectHeightImage / 2));
        const actualRectWidth = Math.min(rectWidthImage, imageObj.width - sourceX);
        const actualRectHeight = Math.min(rectHeightImage, imageObj.height - sourceY);

        // STEP 1: Create canvas1 and draw the bounding rectangle from the original image
        const canvas1 = document.createElement('canvas');
        const ctx1 = canvas1.getContext('2d');

        // Set canvas1 size to accommodate the rotated rectangle (use larger dimensions to avoid clipping)
        const maxDimension = Math.max(actualRectWidth, actualRectHeight) * 2; // 2x for safety margin
        canvas1.width = maxDimension;
        canvas1.height = maxDimension;

        // Draw the rectangle area from the original image to canvas1 (centered)
        const canvas1CenterX = maxDimension / 2;
        const canvas1CenterY = maxDimension / 2;

        // Clear canvas with white background
        ctx1.fillStyle = 'white';
        ctx1.fillRect(0, 0, maxDimension, maxDimension);

        ctx1.drawImage(
            imageObj,
            sourceX, sourceY, actualRectWidth, actualRectHeight,
            canvas1CenterX - actualRectWidth / 2, canvas1CenterY - actualRectHeight / 2,
            actualRectWidth, actualRectHeight
        );

        // STEP 2: Create canvas2 and draw canvas1 with -1 * angle rotation
        const canvas2 = document.createElement('canvas');
        const ctx2 = canvas2.getContext('2d');
        canvas2.width = maxDimension;
        canvas2.height = maxDimension;

        // Apply rotation (negative angle to counter-rotate)
        const canvas2CenterX = maxDimension / 2;
        const canvas2CenterY = maxDimension / 2;

        // Clear canvas with white background
        ctx2.fillStyle = 'white';
        ctx2.fillRect(0, 0, maxDimension, maxDimension);

        ctx2.save();
        ctx2.translate(canvas2CenterX, canvas2CenterY);
        ctx2.rotate((-rectRotation * Math.PI) / 180); // Negative rotation to straighten
        ctx2.drawImage(canvas1, -maxDimension / 2, -maxDimension / 2);
        ctx2.restore();

        // STEP 3: Crop from center of canvas2 with rectangle dimensions and display on preview canvas
        previewCanvas.width = containerWidth;
        previewCanvas.height = containerHeight;

        const previewCtx = previewCanvas.getContext('2d');
        previewCtx.clearRect(0, 0, containerWidth, containerHeight);

        // Calculate crop area from center of canvas2
        const cropX = canvas2CenterX - actualRectWidth / 2;
        const cropY = canvas2CenterY - actualRectHeight / 2;

        // Calculate display dimensions to fit in preview container
        const previewRatio = actualRectWidth / actualRectHeight;
        let previewWidth, previewHeight;

        previewHeight = containerHeight * 0.9;
        previewWidth = previewHeight * previewRatio;

        // If width is too large, fit by width instead
        if (previewWidth > containerWidth * 0.9) {
            previewWidth = containerWidth * 0.9;
            previewHeight = previewWidth / previewRatio;
        }

        // Center the preview in the container
        const previewX = (containerWidth - previewWidth) / 2;
        const previewY = (containerHeight - previewHeight) / 2;

        // Draw the cropped and straightened image
        previewCtx.drawImage(
            canvas2,
            cropX, cropY, actualRectWidth, actualRectHeight,
            previewX, previewY, previewWidth, previewHeight
        );
    }, [rectPoints, imageObj, centerPoint, originalRectPoints, scale, rectRotation]);

    // Handle preview button click - fetch processed image from API
    const handlePreview = useCallback(async () => {
        if (!rectPoints || !imageObj || !originalRectPoints || !centerPoint) return;

        // Only allow preview if not already in preview mode
        if (showPreview) return;

        // Show preview mode - fetch processed image from API
        setIsProcessingPreview(true); // Start processing indicator

        try {
            // Convert canvas coordinates to image coordinates
            const container = imageContainerRef.current;
            const containerWidth = container.clientWidth;
            const containerHeight = container.clientHeight;

            // Calculate the image center on canvas
            const imageCenterX = containerWidth / 2;
            const imageCenterY = containerHeight / 2;

            // Convert each point from canvas coordinates to image coordinates
            const convertCanvasToImageCoords = (canvasPoint) => {
                // Step 1: Translate canvas point relative to canvas center
                let x = canvasPoint.x - imageCenterX;
                let y = canvasPoint.y - imageCenterY;

                // Step 2: Reverse rotation if any
                if (rotation !== 0) {
                    const angleRad = (-rotation * Math.PI) / 180; // Negative to reverse
                    const cosAngle = Math.cos(angleRad);
                    const sinAngle = Math.sin(angleRad);
                    const rotatedX = x * cosAngle - y * sinAngle;
                    const rotatedY = x * sinAngle + y * cosAngle;
                    x = rotatedX;
                    y = rotatedY;
                }

                // Step 3: Reverse scale transformation
                x = x / scale;
                y = y / scale;

                // Step 4: Translate to image coordinates (center of image is 0,0, so add half dimensions)
                const imageX = x + imageObj.width / 2;
                const imageY = y + imageObj.height / 2;

                return { x: Math.round(imageX), y: Math.round(imageY) };
            };

            // Prepare the rectangle coordinates in image space
            const rectData = {
                TopLeft: convertCanvasToImageCoords(rectPoints.TopLeft),
                TopRight: convertCanvasToImageCoords(rectPoints.TopRight),
                BottomRight: convertCanvasToImageCoords(rectPoints.BottomRight),
                BottomLeft: convertCanvasToImageCoords(rectPoints.BottomLeft)
            };

            // console.log('Rectangle data sent to API:', rectData);            

            let isID_Numberics = isNaN(selectedImage.id) === false;

            if (!isID_Numberics) {
                // For new images, we send the base64 data
                const base64Data = await getImageBase64(imageObj);

                // Make API call
                const response = await api.post(
                    `${global.config.api.domain}/standardize/api/processimage/getimagebyrect`,
                    { base64: base64Data, Rect: rectData },
                    { responseType: 'blob', headers: { 'Content-Type': 'image/png' } }
                );

                if (response.status === 200) {
                    // Get the blob image from response
                    const blob = response.data;
                    const blobUrl = URL.createObjectURL(blob);

                    // Create an image from the blob and display it in preview canvas
                    const previewImage = new Image();
                    previewImage.onload = () => {
                        const previewCanvas = previewCanvasRef.current;
                        if (previewCanvas) {
                            const container = imageContainerRef.current;
                            const containerWidth = container.clientWidth;
                            const containerHeight = container.clientHeight;

                            previewCanvas.width = containerWidth;
                            previewCanvas.height = containerHeight;

                            const previewCtx = previewCanvas.getContext('2d');
                            previewCtx.clearRect(0, 0, containerWidth, containerHeight);

                            // Calculate display dimensions to fit in preview container
                            const previewRatio = previewImage.width / previewImage.height;
                            let previewWidth, previewHeight;

                            previewHeight = containerHeight * 0.9;
                            previewWidth = previewHeight * previewRatio;

                            // If width is too large, fit by width instead
                            if (previewWidth > containerWidth * 0.9) {
                                previewWidth = containerWidth * 0.9;
                                previewHeight = previewWidth / previewRatio;
                            }

                            // Center the preview in the container
                            const previewX = (containerWidth - previewWidth) / 2;
                            const previewY = (containerHeight - previewHeight) / 2;

                            // Draw the processed image onto the canvas
                            previewCtx.drawImage(
                                previewImage,
                                0, 0, previewImage.width, previewImage.height, // Source: entire blob image
                                previewX, previewY, previewWidth, previewHeight // Destination: centered and scaled
                            );

                            console.log('Preview image drawn successfully');
                            setPreviewCompleted(true); // Mark preview as completed
                        }

                        // Clean up the blob URL
                        URL.revokeObjectURL(blobUrl);
                        setIsProcessingPreview(false); // Stop processing indicator
                    };

                    previewImage.onerror = () => {
                        console.error('Failed to load preview image');
                        URL.revokeObjectURL(blobUrl);
                        setIsProcessingPreview(false);
                        // Fallback to local preview rendering
                        renderPreview();
                    };

                    previewImage.src = blobUrl;
                    setShowPreview(true);
                } else {
                    console.error('Failed to fetch processed image:', response.statusText);
                    setIsProcessingPreview(false);
                    // Fallback to local preview rendering
                    renderPreview();
                    setShowPreview(true);
                    setPreviewCompleted(true); // Mark preview as completed even for fallback
                }
            }
            else {
                // For existing images, we send the image ID to update
                const _token = localStorage.getItem('FWToken');
                const response = await api.post(
                    `${global.config.api.domain}/standardize/api/processimage/updatestandardize`,
                    {
                        Token: _token,
                        ID: selectedImage.id,
                        Rect: rectData,
                        ObjectTableName: 'evidence',
                        Type: selectedImage.type
                    }
                );

                if (response.status === 200 || response.status === 204) {
                    // ping check standardization status until completed ..standardize/api/processimage/getstandardizeimages?id=
                    let timeoutCheckStatus = 10 * 60; // Max 10 minutes

                    const checkStatus = async () => {
                        const statusResponse = await api.get(
                            `${global.config.api.domain}/standardize/api/processimage/getstandardizeimages?id=${selectedImage.id}`
                        );

                        if (statusResponse.status === 200) {
                            const res = statusResponse.data;
                            if (res && res.length > 0 && res[0].IDValue && res[0].IDValue !== standardizedImageId) {
                                // complete will return new IDValue for the standardized image
                                const newImageID = res[0].IDValue;
                                console.log('Standardization completed with new image ID:', newImageID);

                                // Store the new image ID for use in confirm
                                setStandardizedImageId(newImageID);

                                // Fetch the new standardized image as blob
                                const imageResponse = await api.get(
                                    `${global.config.api.domain}/fwiq-api/api/image/publicurl?id=${newImageID}&sizemode=full`
                                );

                                if (imageResponse.status === 200) {

                                    // get image url from text response
                                    const blobUrl = (typeof imageResponse.data === 'string' ? imageResponse.data : String(imageResponse.data)).trim();

                                    setUrlNewStandardizedImage(blobUrl); // Store the URL for reference

                                    // Create an image from the blob and display it in preview canvas
                                    const previewImage = new Image();
                                    previewImage.onload = () => {
                                        const previewCanvas = previewCanvasRef.current;
                                        if (previewCanvas) {

                                            const previewCtx = previewCanvas.getContext('2d');
                                            previewCtx.clearRect(0, 0, previewCanvas.width, previewCanvas.height);
                                            previewCtx.rotate(0); // Reset any rotation

                                            let scale = previewCanvas.height / previewImage.height;

                                            previewCtx.scale(scale, scale);

                                            let center = {
                                                x: previewCanvas.width / (2 * scale) - (previewImage.width / 2),
                                                y: previewCanvas.height / (2 * scale) - (previewImage.height / 2)
                                            };

                                            previewCtx.translate(center.x, center.y);

                                            // draw image at center of canvas
                                            previewCtx.drawImage(
                                                previewImage,
                                                0, 0, previewImage.width, previewImage.height, // Source: entire blob image
                                                0, 0, previewImage.width, previewImage.height // Destination: centered
                                            );

                                            setPreviewCompleted(true); // Mark preview as completed
                                        }

                                        // Clean up the blob URL
                                        URL.revokeObjectURL(blobUrl);
                                        setIsProcessingPreview(false); // Stop processing indicator
                                    };

                                    previewImage.onerror = () => {
                                        console.error('Failed to load existing image preview');
                                        URL.revokeObjectURL(blobUrl);
                                        setIsProcessingPreview(false);
                                        // Fallback to local preview rendering
                                        renderPreview();
                                        setShowPreview(true);
                                        setPreviewCompleted(true);
                                    };

                                    previewImage.src = blobUrl;
                                    setShowPreview(true);
                                } else {
                                    console.error('Failed to fetch standardized image:', imageResponse.statusText);
                                    setIsProcessingPreview(false);
                                    // Fallback to local preview rendering
                                    renderPreview();
                                    setShowPreview(true);
                                    setPreviewCompleted(true);
                                }

                            } else {
                                // Not completed yet, wait and retry
                                if (timeoutCheckStatus <= 0) {
                                    console.error('Standardization timed out after maximum wait time');
                                    setIsProcessingPreview(false);
                                    // Fallback to local preview rendering
                                    renderPreview();
                                    setShowPreview(true);
                                    setPreviewCompleted(true);
                                    return;
                                }
                                timeoutCheckStatus -= 2;
                                await new Promise(res => setTimeout(res, 2000)); // Wait 2 seconds
                                return await checkStatus();
                            }
                        } else {
                            //   throw new Error('Failed to check standardization status');
                            console.error('Failed to check standardization status:', statusResponse.statusText);
                            setIsProcessingPreview(false);
                            // Fallback to local preview rendering
                            renderPreview();
                            setShowPreview(true);
                            setPreviewCompleted(true); // Mark preview as completed even for fallback   
                        }
                    };

                    // Start checking status
                    await checkStatus();
                } else {
                    console.error('Failed to initiate standardization:', response.statusText);
                    setIsProcessingPreview(false);
                    // Fallback to local preview rendering
                    renderPreview();
                    setShowPreview(true);
                    setPreviewCompleted(true);
                }
            }
        } catch (error) {
            console.error('Error fetching processed image:', error);
            setIsProcessingPreview(false);
            // Fallback to local preview rendering
            renderPreview();
            setShowPreview(true);
            setPreviewCompleted(true); // Mark preview as completed even for fallback
        }
    }, [rectPoints, imageObj, originalRectPoints, centerPoint, showPreview, renderPreview, scale, rotation, getImageBase64, selectedImage.id, selectedImage.type, standardizedImageId]);

    // Handle confirm button click - Return the standardized image and rectangle coordinates
    const handleConfirm = useCallback(() => {
        if (!rectPoints || !originalRectPoints || !imageObj || !centerPoint) return;

        try {
            setIsProcessingPreview(true); // Start processing indicator

            // For existing images, use the standardized image ID if available
            if (selectedImage.id && standardizedImageId) {
                console.log('Using standardized image for existing image:', standardizedImageId);

                // Return the standardized image ID and URL to the parent component
                if (onDetectRect) {
                    onDetectRect({
                        standardizedImageId: standardizedImageId,
                        standardizedImageUrl: urlNewStandardizedImage,
                        rect: {
                            TopLeft: { x: rectPoints.TopLeft.x, y: rectPoints.TopLeft.y },
                            TopRight: { x: rectPoints.TopRight.x, y: rectPoints.TopRight.y },
                            BottomRight: { x: rectPoints.BottomRight.x, y: rectPoints.BottomRight.y },
                            BottomLeft: { x: rectPoints.BottomLeft.x, y: rectPoints.BottomLeft.y },
                            rotation: rectRotation
                        },
                        originalImage: selectedImage,
                        processedByAPI: true
                    });
                }

                setIsProcessingPreview(false); // Stop processing indicator

                // Close the editor
                onClose();
                return;
            }

            // Method 1: Try using the same API logic as preview (for new images)
            const handleConfirmWithAPI = async () => {
                try {
                    // Convert canvas coordinates to image coordinates
                    const container = imageContainerRef.current;
                    const containerWidth = container.clientWidth;
                    const containerHeight = container.clientHeight;

                    // Calculate the image center on canvas
                    const imageCenterX = containerWidth / 2;
                    const imageCenterY = containerHeight / 2;

                    // Convert each point from canvas coordinates to image coordinates
                    const convertCanvasToImageCoords = (canvasPoint) => {
                        // Step 1: Translate canvas point relative to canvas center
                        let x = canvasPoint.x - imageCenterX;
                        let y = canvasPoint.y - imageCenterY;

                        // Step 2: Reverse rotation if any
                        if (rotation !== 0) {
                            const angleRad = (-rotation * Math.PI) / 180; // Negative to reverse
                            const cosAngle = Math.cos(angleRad);
                            const sinAngle = Math.sin(angleRad);
                            const rotatedX = x * cosAngle - y * sinAngle;
                            const rotatedY = x * sinAngle + y * cosAngle;
                            x = rotatedX;
                            y = rotatedY;
                        }

                        // Step 3: Reverse scale transformation
                        x = x / scale;
                        y = y / scale;

                        // Step 4: Translate to image coordinates (center of image is 0,0, so add half dimensions)
                        const imageX = x + imageObj.width / 2;
                        const imageY = y + imageObj.height / 2;

                        return {
                            x: Math.max(0, Math.min(imageObj.width, Math.round(imageX))),
                            y: Math.max(0, Math.min(imageObj.height, Math.round(imageY)))
                        };
                    };

                    // Prepare the rectangle coordinates in image space
                    const rectData = {
                        TopLeft: convertCanvasToImageCoords(rectPoints.TopLeft),
                        TopRight: convertCanvasToImageCoords(rectPoints.TopRight),
                        BottomRight: convertCanvasToImageCoords(rectPoints.BottomRight),
                        BottomLeft: convertCanvasToImageCoords(rectPoints.BottomLeft)
                    };

                    // Check if this is an existing image with urlNewStandardizedImage from preview
                    if (selectedImage.id && urlNewStandardizedImage) {
                        console.log('Using existing standardized image from preview:', urlNewStandardizedImage);

                        // Fetch the standardized image and convert to base64
                        const response = await fetch(urlNewStandardizedImage);
                        if (response.ok) {
                            const blob = await response.blob();

                            // Convert blob to base64
                            const reader = new FileReader();
                            reader.onloadend = () => {
                                const processedBase64 = reader.result; // This includes the data:image/jpeg;base64, prefix

                                console.log('Using standardized image from preview successfully:', {
                                    originalSize: { width: imageObj.width, height: imageObj.height },
                                    rectData,
                                    base64Length: processedBase64.length,
                                    standardizedImageId: standardizedImageId
                                });

                                // Return the standardized image from preview
                                if (onDetectRect) {
                                    onDetectRect({
                                        base64: processedBase64,
                                        rect: {
                                            TopLeft: convertCanvasToImageCoords(rectPoints.TopLeft),
                                            TopRight: convertCanvasToImageCoords(rectPoints.TopRight),
                                            BottomRight: convertCanvasToImageCoords(rectPoints.BottomRight),
                                            BottomLeft: convertCanvasToImageCoords(rectPoints.BottomLeft),
                                            rotation: rectRotation
                                        },
                                        originalImage: selectedImage,
                                        standardizedImageId: standardizedImageId,
                                        standardizedImageUrl: urlNewStandardizedImage,
                                        processedByAPI: true
                                    });
                                }

                                // Close the editor
                                onClose();
                            };
                            reader.readAsDataURL(blob);
                            return; // Exit here if using existing standardized image succeeds
                        } else {
                            console.warn('Failed to fetch existing standardized image, falling back to API processing');
                        }
                    }

                    // For new images or when existing standardized image fetch fails, use API processing
                    // Get the base64 image data using CORS-safe method
                    const base64Data = await getImageBase64(imageObj);

                    // Make API call to get processed image
                    const response = await api.post(
                        `${global.config.api.domain}/standardize/api/processimage/getimagebyrect`,
                        { base64: base64Data, Rect: rectData },
                        { responseType: 'blob' }
                    );

                    if (response.status === 200) {
                        // Get the processed image blob
                        const blob = response.data;

                        // Convert blob to base64
                        const reader = new FileReader();
                        reader.onloadend = () => {
                            const processedBase64 = reader.result; // This includes the data:image/jpeg;base64, prefix

                            console.log('API processed image successfully:', {
                                originalSize: { width: imageObj.width, height: imageObj.height },
                                rectData,
                                base64Length: processedBase64.length
                            });

                            // Return the API-processed image
                            if (onDetectRect) {
                                onDetectRect({
                                    base64: processedBase64,
                                    rect: {
                                        TopLeft: convertCanvasToImageCoords(rectPoints.TopLeft),
                                        TopRight: convertCanvasToImageCoords(rectPoints.TopRight),
                                        BottomRight: convertCanvasToImageCoords(rectPoints.BottomRight),
                                        BottomLeft: convertCanvasToImageCoords(rectPoints.BottomLeft),
                                        rotation: rectRotation
                                    },
                                    originalImage: selectedImage,
                                    processedByAPI: true
                                });
                            }

                            // Close the editor
                            onClose();
                        };
                        reader.readAsDataURL(blob);
                        return; // Exit here if API succeeds
                    } else {
                        console.warn('API processing failed, falling back to local processing');
                        throw new Error('API processing failed');
                    }
                } catch (apiError) {
                    console.warn('API processing error, falling back to local processing:', apiError);
                    throw apiError; // Let it fall through to local processing
                }
            };

            // Try API first, then fall back to local processing
            handleConfirmWithAPI().catch(() => {
                // Local processing fallback
                console.log('Using local processing for image standardization');

                const container = imageContainerRef.current;
                if (!container) return;

                const containerWidth = container.clientWidth;
                const containerHeight = container.clientHeight;

                // Get rectangle dimensions from original unrotated points
                const rectWidth = Math.abs(originalRectPoints.TopRight.x - originalRectPoints.TopLeft.x);
                const rectHeight = Math.abs(originalRectPoints.BottomLeft.y - originalRectPoints.TopLeft.y);

                // Calculate how the original image is displayed on the canvas
                // (This is needed for coordinate conversion, even though we now use direct scaling)

                // Calculate the image center on canvas
                const imageCenterX = containerWidth / 2;
                const imageCenterY = containerHeight / 2;

                // Helper function to convert canvas coordinates to image coordinates
                const convertToImageCoords = (canvasPoint) => {
                    // Step 1: Translate canvas point relative to canvas center
                    let x = canvasPoint.x - imageCenterX;
                    let y = canvasPoint.y - imageCenterY;

                    // Step 2: Reverse rotation if any
                    if (rotation !== 0) {
                        const angleRad = (-rotation * Math.PI) / 180; // Negative to reverse
                        const cosAngle = Math.cos(angleRad);
                        const sinAngle = Math.sin(angleRad);
                        const rotatedX = x * cosAngle - y * sinAngle;
                        const rotatedY = x * sinAngle + y * cosAngle;
                        x = rotatedX;
                        y = rotatedY;
                    }

                    // Step 3: Reverse scale transformation
                    x = x / scale;
                    y = y / scale;

                    // Step 4: Translate to image coordinates
                    const imageX = x + imageObj.width / 2;
                    const imageY = y + imageObj.height / 2;

                    return { x: imageX, y: imageY };
                };

                // Convert rectangle center coordinates to image coordinates
                const centerImageCoords = convertToImageCoords(centerPoint);
                const rectCenterImageX = centerImageCoords.x;
                const rectCenterImageY = centerImageCoords.y;

                // Calculate rectangle dimensions in image space
                const rectWidthImage = rectWidth / scale;
                const rectHeightImage = rectHeight / scale;

                // Ensure rectangle bounds are within image boundaries
                const sourceX = Math.max(0, Math.min(imageObj.width - 1, rectCenterImageX - rectWidthImage / 2));
                const sourceY = Math.max(0, Math.min(imageObj.height - 1, rectCenterImageY - rectHeightImage / 2));
                const actualRectWidth = Math.min(rectWidthImage, imageObj.width - sourceX);
                const actualRectHeight = Math.min(rectHeightImage, imageObj.height - sourceY);

                // Ensure dimensions are positive
                if (actualRectWidth <= 0 || actualRectHeight <= 0) {
                    console.error('Invalid rectangle dimensions:', { actualRectWidth, actualRectHeight });
                    return;
                }

                // STEP 1: Create canvas1 and draw the bounding rectangle from the original image
                const canvas1 = document.createElement('canvas');
                const ctx1 = canvas1.getContext('2d');

                // Set canvas1 size to accommodate the rotated rectangle with safety margin
                const maxDimension = Math.max(actualRectWidth, actualRectHeight) * 3; // 3x for extra safety
                canvas1.width = maxDimension;
                canvas1.height = maxDimension;

                const canvas1CenterX = maxDimension / 2;
                const canvas1CenterY = maxDimension / 2;

                // Clear canvas with white background
                ctx1.fillStyle = 'white';
                ctx1.fillRect(0, 0, maxDimension, maxDimension);

                // Draw the rectangle area from the original image to canvas1 (centered)
                try {
                    ctx1.drawImage(
                        imageObj,
                        Math.max(0, sourceX), Math.max(0, sourceY),
                        Math.min(actualRectWidth, imageObj.width - Math.max(0, sourceX)),
                        Math.min(actualRectHeight, imageObj.height - Math.max(0, sourceY)),
                        canvas1CenterX - actualRectWidth / 2, canvas1CenterY - actualRectHeight / 2,
                        actualRectWidth, actualRectHeight
                    );
                } catch (drawError) {
                    console.error('Error drawing to canvas1:', drawError);
                    throw drawError;
                }

                // STEP 2: Create canvas2 and draw canvas1 with counter-rotation
                const canvas2 = document.createElement('canvas');
                const ctx2 = canvas2.getContext('2d');
                canvas2.width = maxDimension;
                canvas2.height = maxDimension;

                const canvas2CenterX = maxDimension / 2;
                const canvas2CenterY = maxDimension / 2;

                // Clear canvas with white background
                ctx2.fillStyle = 'white';
                ctx2.fillRect(0, 0, maxDimension, maxDimension);

                ctx2.save();
                ctx2.translate(canvas2CenterX, canvas2CenterY);
                ctx2.rotate((-rectRotation * Math.PI) / 180); // Counter-rotate to straighten
                ctx2.drawImage(canvas1, -maxDimension / 2, -maxDimension / 2);
                ctx2.restore();

                // STEP 3: Create final standardized canvas with exact dimensions
                const standardizedCanvas = document.createElement('canvas');
                const standardizedCtx = standardizedCanvas.getContext('2d');

                // Use rounded dimensions to avoid fractional pixels
                standardizedCanvas.width = Math.max(1, Math.round(actualRectWidth));
                standardizedCanvas.height = Math.max(1, Math.round(actualRectHeight));

                // Clear canvas with white background
                standardizedCtx.fillStyle = 'white';
                standardizedCtx.fillRect(0, 0, standardizedCanvas.width, standardizedCanvas.height);

                // Crop from center of canvas2
                const cropX = canvas2CenterX - actualRectWidth / 2;
                const cropY = canvas2CenterY - actualRectHeight / 2;

                // Draw the final standardized image
                try {
                    standardizedCtx.drawImage(
                        canvas2,
                        cropX, cropY, actualRectWidth, actualRectHeight,
                        0, 0, standardizedCanvas.width, standardizedCanvas.height
                    );
                } catch (finalDrawError) {
                    console.error('Error drawing final image:', finalDrawError);
                    throw finalDrawError;
                }

                // Get the standardized image as base64 with high quality
                const standardizedBase64 = getCanvasBase64Safe(standardizedCanvas, 0.95);

                // Convert rectangle coordinates to image space with proper bounds checking
                const convertCanvasToImageCoords = (canvasPoint) => {
                    // Step 1: Translate canvas point relative to canvas center
                    let x = canvasPoint.x - imageCenterX;
                    let y = canvasPoint.y - imageCenterY;

                    // Step 2: Reverse rotation if any
                    if (rotation !== 0) {
                        const angleRad = (-rotation * Math.PI) / 180; // Negative to reverse
                        const cosAngle = Math.cos(angleRad);
                        const sinAngle = Math.sin(angleRad);
                        const rotatedX = x * cosAngle - y * sinAngle;
                        const rotatedY = x * sinAngle + y * cosAngle;
                        x = rotatedX;
                        y = rotatedY;
                    }

                    // Step 3: Reverse scale transformation
                    x = x / scale;
                    y = y / scale;

                    // Step 4: Translate to image coordinates
                    const imageX = x + imageObj.width / 2;
                    const imageY = y + imageObj.height / 2;

                    return {
                        x: Math.max(0, Math.min(imageObj.width - 1, Math.round(imageX))),
                        y: Math.max(0, Math.min(imageObj.height - 1, Math.round(imageY)))
                    };
                };

                const rectData = {
                    TopLeft: convertCanvasToImageCoords(rectPoints.TopLeft),
                    TopRight: convertCanvasToImageCoords(rectPoints.TopRight),
                    BottomRight: convertCanvasToImageCoords(rectPoints.BottomRight),
                    BottomLeft: convertCanvasToImageCoords(rectPoints.BottomLeft),
                    rotation: rectRotation
                };

                console.log('Generated standardized image locally:', {
                    originalSize: { width: imageObj.width, height: imageObj.height },
                    rectSize: { width: actualRectWidth, height: actualRectHeight },
                    standardizedSize: { width: standardizedCanvas.width, height: standardizedCanvas.height },
                    rectData,
                    base64Length: standardizedBase64.length
                });

                // Return the locally processed image
                if (onDetectRect) {
                    onDetectRect({
                        base64: standardizedBase64,
                        rect: rectData,
                        originalImage: selectedImage,
                        standardizedSize: {
                            width: standardizedCanvas.width,
                            height: standardizedCanvas.height
                        },
                        processedLocally: true
                    });
                }

                setIsProcessingPreview(false); // Stop processing indicator

                // Close the editor
                onClose();
            });

        } catch (error) {
            console.error('Error in handleConfirm:', error);

            // Final fallback: Return original image with coordinates
            try {
                const fallbackCanvas = document.createElement('canvas');
                const fallbackCtx = fallbackCanvas.getContext('2d');

                fallbackCanvas.width = imageObj.width;
                fallbackCanvas.height = imageObj.height;

                // Draw original image
                fallbackCtx.drawImage(imageObj, 0, 0);

                // Get base64 of original image
                const originalBase64 = getCanvasBase64Safe(fallbackCanvas, 0.9);

                console.log('Using fallback - returning original image with coordinates');

                if (onDetectRect) {
                    onDetectRect({
                        base64: originalBase64,
                        rect: {
                            TopLeft: rectPoints.TopLeft,
                            TopRight: rectPoints.TopRight,
                            BottomRight: rectPoints.BottomRight,
                            BottomLeft: rectPoints.BottomLeft,
                            rotation: rectRotation
                        },
                        originalImage: selectedImage,
                        isFallback: true,
                        error: error.message
                    });
                }

                // Close the editor
                onClose();
            } catch (fallbackError) {
                console.error('Even fallback failed:', fallbackError);
                onClose(); // Close anyway
            }
        }
    }, [rectPoints, originalRectPoints, imageObj, centerPoint, rectRotation, scale, rotation, onDetectRect, onClose, selectedImage, getImageBase64, getCanvasBase64Safe, standardizedImageId, urlNewStandardizedImage]);

    // Handle edit current standardization - go back to editing state before preview
    const handleEditCurrentStandardization = useCallback(() => {
        console.log('Returning to edit mode from preview');

        // Reset preview states but keep rectangle and its current position/rotation
        setShowPreview(false);
        setIsProcessingPreview(false);
        setPreviewCompleted(false);

        // Clear preview canvas
        const previewCanvas = previewCanvasRef.current;
        if (previewCanvas) {
            const previewCtx = previewCanvas.getContext('2d');
            previewCtx.clearRect(0, 0, previewCanvas.width, previewCanvas.height);
        }

        // The useEffect hook will handle redrawing the main canvas automatically
        // when showPreview becomes false, so we don't need to do anything else here

    }, []);

    // Handle color picker
    const handleOpenColorPicker = useCallback(() => {
        setShowColorPicker(true);
    }, []);

    const handleCloseColorPicker = useCallback(() => {
        setShowColorPicker(false);
    }, []);

    const handleColorsChange = useCallback((newColors) => {
        setColors(newColors);
    }, []);

    // Reset everything to original state
    const handleCancel = () => {
        // Reset all transformations
        setScale(defaultScaleForReset);
        setRotation(0);
        prevScaleRef.current = defaultScaleForReset; // Update ref to match reset scale

        // Clear rectangle detection states
        setRectPoints(null);
        setOriginalRectPoints(null);
        setIsDraggingPoint(false);
        setActivePointIndex(null);
        setArrowPoint(null);
        setRectRotation(0);
        setIsRotating(false);
        setCenterPoint(null);
        setShowPreview(false);
        setPreviewCompleted(false); // Reset preview completion
        setRectDetected(false);
        setMousePosition({ x: 0, y: 0 });
        setDragOffset({ x: 0, y: 0 });

        // Redraw the image at original state
        if (imageObj) {
            drawImageOnCanvas(imageObj);
        }
    };

    // Reset transformations
    const handleReset = () => {
        setScale(defaultScaleForReset);
        setRotation(0);
        prevScaleRef.current = defaultScaleForReset; // Update ref to match reset scale
    };

    // Function to draw the image on the canvas
    const drawImageOnCanvas = useCallback((image) => {
        const canvas = canvasRef.current;
        if (!canvas || !imageContainerRef.current) return;

        const container = imageContainerRef.current;
        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;

        // Set canvas dimensions
        canvas.width = containerWidth;
        canvas.height = containerHeight;

        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Calculate scaled image dimensions for logging
        const imageScaledWidth = image.width * scale;
        const imageScaledHeight = image.height * scale;

        // Save the current context state
        ctx.save();

        // Move to the center for rotation
        ctx.translate(canvas.width / 2, canvas.height / 2);

        // Rotate around this point
        ctx.rotate((rotation * Math.PI) / 180);

        // Apply scale transformation
        ctx.scale(scale, scale);

        // Draw the image centered using original image dimensions
        ctx.drawImage(
            image,
            -image.width / 2,   // Center horizontally
            -image.height / 2,  // Center vertically
            image.width,        // Use original image width
            image.height        // Use original image height
        );

        // Restore the context state
        ctx.restore();

        // Log to debug
        // console.log("Canvas redrawn with simplified scale:", {
        //     imageSize: { width: image.width, height: image.height },
        //     containerSize: { width: containerWidth, height: containerHeight },
        //     scaledSize: { width: imageScaledWidth, height: imageScaledHeight },
        //     scale: scale
        // });

    }, [scale, rotation]);
    // Function to rotate a point around a center point
    const rotatePoint = useCallback((point, center, angleDegrees) => {
        const angleRad = (angleDegrees * Math.PI) / 180;
        const cosAngle = Math.cos(angleRad);
        const sinAngle = Math.sin(angleRad);

        // Translate point to origin
        const dx = point.x - center.x;
        const dy = point.y - center.y;

        // Rotate point
        const rotatedX = dx * cosAngle - dy * sinAngle + center.x;
        const rotatedY = dx * sinAngle + dy * cosAngle + center.y;

        return { x: rotatedX, y: rotatedY };
    }, []);

    // Function to calculate all control points for a rotated rectangle
    const calculateControlPoints = useCallback((centerX, centerY, width, height, angleDegrees) => {
        // Half-dimensions
        const halfWidth = width / 2;
        const halfHeight = height / 2;

        // Create unrotated points object
        const unrotatedPoints = {
            TopLeft: { x: centerX - halfWidth, y: centerY - halfHeight },
            CenterTop: { x: centerX, y: centerY - halfHeight },
            TopRight: { x: centerX + halfWidth, y: centerY - halfHeight },
            CenterRight: { x: centerX + halfWidth, y: centerY },
            BottomRight: { x: centerX + halfWidth, y: centerY + halfHeight },
            CenterBottom: { x: centerX, y: centerY + halfHeight },
            BottomLeft: { x: centerX - halfWidth, y: centerY + halfHeight },
            CenterLeft: { x: centerX - halfWidth, y: centerY }
        };

        // Rotate all points
        const center = { x: centerX, y: centerY };
        const rotatedPoints = {
            TopLeft: rotatePoint(unrotatedPoints.TopLeft, center, angleDegrees),
            CenterTop: rotatePoint(unrotatedPoints.CenterTop, center, angleDegrees),
            TopRight: rotatePoint(unrotatedPoints.TopRight, center, angleDegrees),
            CenterRight: rotatePoint(unrotatedPoints.CenterRight, center, angleDegrees),
            BottomRight: rotatePoint(unrotatedPoints.BottomRight, center, angleDegrees),
            CenterBottom: rotatePoint(unrotatedPoints.CenterBottom, center, angleDegrees),
            BottomLeft: rotatePoint(unrotatedPoints.BottomLeft, center, angleDegrees),
            CenterLeft: rotatePoint(unrotatedPoints.CenterLeft, center, angleDegrees)
        };

        return rotatedPoints;
    }, [rotatePoint]);

    const drawImageWithRect = useCallback((image, points, arrow) => {
        const canvas = canvasRef.current;
        if (!canvas || !imageContainerRef.current || !points || !arrow) return;

        // First draw the base image
        drawImageOnCanvas(image);

        const ctx = canvas.getContext('2d');

        // Calculate center point from the passed-in points
        const centerX = (points.TopLeft.x + points.BottomRight.x) / 2;
        const centerY = (points.TopLeft.y + points.BottomRight.y) / 2;
        setCenterPoint({ x: centerX, y: centerY });

        // Draw the rectangle connecting the points (use the actual rotated points)
        ctx.beginPath();
        ctx.moveTo(points.TopLeft.x, points.TopLeft.y);
        ctx.lineTo(points.TopRight.x, points.TopRight.y);
        ctx.lineTo(points.BottomRight.x, points.BottomRight.y);
        ctx.lineTo(points.BottomLeft.x, points.BottomLeft.y);
        ctx.closePath();
        ctx.strokeStyle = colors.blue;
        ctx.lineWidth = 2;
        // Use dashed line when user is dragging/resizing/moving the rectangle
        if (isDraggingPoint || isRotating) {
            ctx.setLineDash([5, 5]); // 5px dash, 5px gap
        } else {
            ctx.setLineDash([]); // Solid line
        }

        ctx.stroke();

        // Reset line dash for other elements
        ctx.setLineDash([]);

        // Draw center point of the rectangle (for dragging)
        ctx.beginPath();
        ctx.arc(centerX, centerY, 10, 0, 2 * Math.PI);
        ctx.fillStyle = activePointIndex === 9 ? colors.green : colors.yellow;
        ctx.fill();
        // ctx.stroke();

        // Calculate direction to always point up (towards top), adjusted for rectangle rotation
        const arrowLength = 60; // Fixed length for the arrow

        // Direction vector, rotated based on rectangle rotation
        const angleRad = (rectRotation * Math.PI) / 180;
        const dirX = Math.sin(angleRad);  // Rotated direction vector
        const dirY = -Math.cos(angleRad); // Always point up, but adjusted for rotation

        // Calculate the arrow end point
        const arrowEndX = centerX + dirX * arrowLength;
        const arrowEndY = centerY + dirY * arrowLength;

        // Draw arrow from center to arrow end point
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.lineTo(arrowEndX, arrowEndY);
        ctx.strokeStyle = colors.red;
        ctx.lineWidth = 2;
        ctx.stroke();

        // Draw arrowhead
        const headLength = 15;
        const angle = Math.atan2(dirY, dirX); // Use the rotated direction vector for angle
        ctx.beginPath();
        ctx.moveTo(arrowEndX, arrowEndY);
        ctx.lineTo(
            arrowEndX - headLength * Math.cos(angle - Math.PI / 6),
            arrowEndY - headLength * Math.sin(angle - Math.PI / 6)
        );
        ctx.moveTo(arrowEndX, arrowEndY);
        ctx.lineTo(
            arrowEndX - headLength * Math.cos(angle + Math.PI / 6),
            arrowEndY - headLength * Math.sin(angle + Math.PI / 6)
        );
        ctx.stroke();

        // Update the arrow point for hit detection and dragging
        arrow.x = arrowEndX;
        arrow.y = arrowEndY;

        // Restore canvas state for drawing control points (unrotated)
        ctx.restore();

        // Draw control points using the actual rectPoints (which are already properly rotated)
        const pointKeys = ['TopLeft', 'CenterTop', 'TopRight', 'CenterRight', 'BottomRight', 'CenterBottom', 'BottomLeft', 'CenterLeft'];
        pointKeys.forEach((key, index) => {
            const point = points[key]; // Use the passed-in points parameter which contains the correct rotated positions
            ctx.beginPath();
            ctx.arc(point.x, point.y, 8, 0, 2 * Math.PI);
            ctx.fillStyle = activePointIndex === index ? colors.green : colors.blue;

            if (activePointIndex !== index)
                ctx.fill();

            ctx.strokeStyle = activePointIndex === index ? colors.green : colors.red;
            ctx.stroke();
        });

        // Draw rotate icon at mouse position if rotating
        if (isRotating) {
            drawIconRotate(ctx, mousePosition.x, mousePosition.y);
        }

        // Return the points for any caller that needs them
        return points;
    }, [drawImageOnCanvas, activePointIndex, rectRotation, isRotating, mousePosition, drawIconRotate, isDraggingPoint, colors]);

    // Mouse event handlers for dragging points
    const handleMouseDown = useCallback((e) => {
        if (!rectPoints || !arrowPoint) return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        // Handle right-click for rotation
        if (e.button === 2) { // Right mouse button

            // Rotation mode: save start rotate point and initial angle


            e.preventDefault(); // Prevent context menu            // Use centerPoint if available, or calculate center of the rectangle
            const centerX = centerPoint ? centerPoint.x : (rectPoints.TopLeft.x + rectPoints.BottomRight.x) / 2;
            const centerY = centerPoint ? centerPoint.y : (rectPoints.TopLeft.y + rectPoints.BottomRight.y) / 2;

            // Start rotation mode
            setIsRotating(true);

            // Calculate the initial angle between mouse position and center
            const initialAngle = Math.atan2(y - centerY, x - centerX) * (180 / Math.PI);

            // Store initial angle and current rotation for reference
            canvas.setAttribute('data-initial-angle', initialAngle);
            canvas.setAttribute('data-current-rotation', rectRotation);

            return;
        }

        // Check if we're clicking on any of the control points
        let pointIndex = -1;        // Check the 8 rectangle control points
        const pointKeys = ['TopLeft', 'CenterTop', 'TopRight', 'CenterRight', 'BottomRight', 'CenterBottom', 'BottomLeft', 'CenterLeft'];
        for (let i = 0; i < pointKeys.length; i++) {
            const point = rectPoints[pointKeys[i]];
            const distance = Math.sqrt(Math.pow(point.x - x, 2) + Math.pow(point.y - y, 2));

            if (distance <= 10) { // 10 pixels is the hit area for control points
                pointIndex = i;
                break;
            }
        }

        // Check the arrow control point
        if (pointIndex === -1) {
            const distance = Math.sqrt(Math.pow(arrowPoint.x - x, 2) + Math.pow(arrowPoint.y - y, 2));
            if (distance <= 10) {
                pointIndex = 8; // 8 is the index for the arrow point
            }
        }        // Check if clicking on the center point
        if (pointIndex === -1 && centerPoint) {
            const distance = Math.sqrt(Math.pow(centerPoint.x - x, 2) + Math.pow(centerPoint.y - y, 2));
            if (distance <= 10) {
                pointIndex = 9; // 9 is the index for the center point (for moving the entire rectangle)
                setDragOffset({ x: 0, y: 0 }); // No offset when clicking directly on center
            }
        }

        // Check if clicking inside the rectangle (for moving the entire rectangle)
        if (pointIndex === -1) {
            // Use a point-in-polygon algorithm to check if click is inside the rectangle
            const isInsideRectangle = (point, rectPoints) => {
                // Create polygon from the 4 corner points
                const polygon = [
                    rectPoints.TopLeft,
                    rectPoints.TopRight,
                    rectPoints.BottomRight,
                    rectPoints.BottomLeft
                ];

                let inside = false;
                for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
                    if (((polygon[i].y > point.y) !== (polygon[j].y > point.y)) &&
                        (point.x < (polygon[j].x - polygon[i].x) * (point.y - polygon[i].y) / (polygon[j].y - polygon[i].y) + polygon[i].x)) {
                        inside = !inside;
                    }
                }
                return inside;
            }; if (isInsideRectangle({ x, y }, rectPoints)) {
                pointIndex = 9; // Treat as center point drag (move entire rectangle)

                // Calculate offset from center for smooth dragging
                if (centerPoint) {
                    setDragOffset({
                        x: x - centerPoint.x,
                        y: y - centerPoint.y
                    });
                }
            }
        }

        if (pointIndex !== -1) {
            setIsDraggingPoint(true);
            setActivePointIndex(pointIndex);
        }
    }, [rectPoints, arrowPoint, centerPoint, rectRotation]);

    const handleMouseMove = useCallback((e) => {
        if ((!isDraggingPoint && !isRotating) || (isDraggingPoint && activePointIndex === null) || !rectPoints || !originalRectPoints) return;

        const canvas = canvasRef.current;
        if (!canvas) return; const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Update mouse position for rotate icon
        setMousePosition({ x, y });

        // Handle rotation
        if (isRotating) {
            // Calculate center of the rectangle
            const centerX = centerPoint ? centerPoint.x : (rectPoints.TopLeft.x + rectPoints.BottomRight.x) / 2;
            const centerY = centerPoint ? centerPoint.y : (rectPoints.TopLeft.y + rectPoints.BottomRight.y) / 2;

            // Calculate the current angle between mouse position and center
            const currentAngle = Math.atan2(y - centerY, x - centerX) * (180 / Math.PI);

            // Get initial values from attributes
            const initialAngle = parseFloat(canvas.getAttribute('data-initial-angle') || "0");
            const currentRotation = parseFloat(canvas.getAttribute('data-current-rotation') || "0");

            // Calculate the angle difference
            let angleDelta = currentAngle - initialAngle;

            // Update rectangle rotation
            let newRotation = (currentRotation + angleDelta) % 360;
            if (newRotation < 0) newRotation += 360;

            // Update rotation state
            setRectRotation(newRotation);            // Recalculate all rotated points from the original points
            if (originalRectPoints && centerPoint) {
                const rotatedPoints = calculateControlPoints(
                    centerPoint.x,
                    centerPoint.y,
                    Math.abs(originalRectPoints.TopRight.x - originalRectPoints.TopLeft.x),
                    Math.abs(originalRectPoints.BottomLeft.y - originalRectPoints.TopLeft.y),
                    newRotation
                );
                setRectPoints(rotatedPoints);

                if (imageObj && arrowPoint) {
                    drawImageWithRect(imageObj, rotatedPoints, arrowPoint);
                }
            }

            return;
        }        // Get the center point of the rectangle
        const centerX = centerPoint ? centerPoint.x : (rectPoints.TopLeft.x + rectPoints.BottomRight.x) / 2;
        const centerY = centerPoint ? centerPoint.y : (rectPoints.TopLeft.y + rectPoints.BottomRight.y) / 2;

        // Use the original unrotated points for dimension calculations
        const width = Math.abs(originalRectPoints.TopRight.x - originalRectPoints.TopLeft.x);
        const height = Math.abs(originalRectPoints.BottomLeft.y - originalRectPoints.TopLeft.y);

        // Handle special cases
        if (activePointIndex === 8) {
            // Moving the arrow point
            const newArrowPoint = { x, y };
            setArrowPoint(newArrowPoint);

            if (imageObj) {
                drawImageWithRect(imageObj, rectPoints, newArrowPoint);
            }
        } else if (activePointIndex === 9) {
            // Moving the entire rectangle by its center point or from inside the rectangle
            if (centerPoint) {
                // Calculate the target center position accounting for drag offset
                const targetCenterX = x - dragOffset.x;
                const targetCenterY = y - dragOffset.y;

                const deltaX = targetCenterX - centerPoint.x;
                const deltaY = targetCenterY - centerPoint.y;

                // Update the original unrotated points by moving each point
                const newOriginalPoints = {
                    TopLeft: { x: originalRectPoints.TopLeft.x + deltaX, y: originalRectPoints.TopLeft.y + deltaY },
                    CenterTop: { x: originalRectPoints.CenterTop.x + deltaX, y: originalRectPoints.CenterTop.y + deltaY },
                    TopRight: { x: originalRectPoints.TopRight.x + deltaX, y: originalRectPoints.TopRight.y + deltaY },
                    CenterRight: { x: originalRectPoints.CenterRight.x + deltaX, y: originalRectPoints.CenterRight.y + deltaY },
                    BottomRight: { x: originalRectPoints.BottomRight.x + deltaX, y: originalRectPoints.BottomRight.y + deltaY },
                    CenterBottom: { x: originalRectPoints.CenterBottom.x + deltaX, y: originalRectPoints.CenterBottom.y + deltaY },
                    BottomLeft: { x: originalRectPoints.BottomLeft.x + deltaX, y: originalRectPoints.BottomLeft.y + deltaY },
                    CenterLeft: { x: originalRectPoints.CenterLeft.x + deltaX, y: originalRectPoints.CenterLeft.y + deltaY }
                };

                // Apply rotation to get the display points
                const newCenter = { x: centerPoint.x + deltaX, y: centerPoint.y + deltaY };
                const newPoints = {
                    TopLeft: rotatePoint(newOriginalPoints.TopLeft, newCenter, rectRotation),
                    CenterTop: rotatePoint(newOriginalPoints.CenterTop, newCenter, rectRotation),
                    TopRight: rotatePoint(newOriginalPoints.TopRight, newCenter, rectRotation),
                    CenterRight: rotatePoint(newOriginalPoints.CenterRight, newCenter, rectRotation),
                    BottomRight: rotatePoint(newOriginalPoints.BottomRight, newCenter, rectRotation),
                    CenterBottom: rotatePoint(newOriginalPoints.CenterBottom, newCenter, rectRotation),
                    BottomLeft: rotatePoint(newOriginalPoints.BottomLeft, newCenter, rectRotation),
                    CenterLeft: rotatePoint(newOriginalPoints.CenterLeft, newCenter, rectRotation)
                };

                // Update states
                setOriginalRectPoints(newOriginalPoints);
                setRectPoints(newPoints);
                setCenterPoint({ x: centerPoint.x + deltaX, y: centerPoint.y + deltaY });

                if (imageObj && arrowPoint) {
                    const newArrowPoint = {
                        x: arrowPoint.x + deltaX,
                        y: arrowPoint.y + deltaY
                    };
                    setArrowPoint(newArrowPoint);
                    drawImageWithRect(imageObj, newPoints, newArrowPoint);
                }
            }
        } else {
            // For control points (0-7): First, de-rotate the mouse point to work in the unrotated coordinate system
            const mousePoint = { x, y };

            // Calculate the center of the original unrotated rectangle for proper de-rotation
            const originalCenter = {
                x: (originalRectPoints.TopLeft.x + originalRectPoints.BottomRight.x) / 2,
                y: (originalRectPoints.TopLeft.y + originalRectPoints.BottomRight.y) / 2
            };

            const derotatedMousePoint = rotatePoint(mousePoint, originalCenter, -rectRotation);            // Use the de-rotated coordinates for calculations
            const derotatedX = derotatedMousePoint.x;
            const derotatedY = derotatedMousePoint.y;

            // Work with the original unrotated rectangle
            let newWidth = width;
            let newHeight = height;
            let newCenterX = centerX;
            let newCenterY = centerY;            // Handle control point movement            
            if (activePointIndex % 2 === 0) {
                // Corner points (TopLeft, TopRight, BottomRight, BottomLeft)
                const cornerIndex = activePointIndex;

                // For corner dragging, keep the opposite corner fixed by creating a new rectangle
                // with the fixed corner and the new dragged corner position
                let fixedCorner, newCorner;

                if (cornerIndex === 0) { // TopLeft - keep BottomRight fixed
                    fixedCorner = originalRectPoints.BottomRight;
                    newCorner = { x: derotatedX, y: derotatedY };
                } else if (cornerIndex === 2) { // TopRight - keep BottomLeft fixed
                    fixedCorner = originalRectPoints.BottomLeft;
                    newCorner = { x: derotatedX, y: derotatedY };
                } else if (cornerIndex === 4) { // BottomRight - keep TopLeft fixed
                    fixedCorner = originalRectPoints.TopLeft;
                    newCorner = { x: derotatedX, y: derotatedY };
                } else if (cornerIndex === 6) { // BottomLeft - keep TopRight fixed
                    fixedCorner = originalRectPoints.TopRight;
                    newCorner = { x: derotatedX, y: derotatedY };
                }

                // Calculate the new rectangle dimensions and center from the fixed and new corners
                const minX = Math.min(fixedCorner.x, newCorner.x);
                const maxX = Math.max(fixedCorner.x, newCorner.x);
                const minY = Math.min(fixedCorner.y, newCorner.y);
                const maxY = Math.max(fixedCorner.y, newCorner.y);
                newWidth = maxX - minX;
                newHeight = maxY - minY;
                newCenterX = (minX + maxX) / 2;
                newCenterY = (minY + maxY) / 2;
            } else {
                // Middle points (1, 3, 5, 7)
                const middleIndex = activePointIndex;

                // Calculate original center for reference
                const originalCenterX = (originalRectPoints.TopLeft.x + originalRectPoints.BottomRight.x) / 2;
                const originalCenterY = (originalRectPoints.TopLeft.y + originalRectPoints.BottomRight.y) / 2;

                // For edge dragging, keep the opposite edge fixed
                if (middleIndex === 1) { // Top center - keep bottom edge fixed
                    const fixedBottomY = originalRectPoints.BottomLeft.y; // or BottomRight.y (same value)
                    newHeight = Math.abs(fixedBottomY - derotatedY);
                    newCenterY = (fixedBottomY + derotatedY) / 2;
                    // Width stays the same
                    newWidth = width;
                    newCenterX = originalCenterX;
                } else if (middleIndex === 3) { // Right center - keep left edge fixed
                    const fixedLeftX = originalRectPoints.TopLeft.x; // or BottomLeft.x (same value)
                    newWidth = Math.abs(derotatedX - fixedLeftX);
                    newCenterX = (fixedLeftX + derotatedX) / 2;
                    // Height stays the same
                    newHeight = height;
                    newCenterY = originalCenterY;
                } else if (middleIndex === 5) { // Bottom center - keep top edge fixed
                    const fixedTopY = originalRectPoints.TopLeft.y; // or TopRight.y (same value)
                    newHeight = Math.abs(derotatedY - fixedTopY);
                    newCenterY = (fixedTopY + derotatedY) / 2;
                    // Width stays the same
                    newWidth = width;
                    newCenterX = originalCenterX;
                } else if (middleIndex === 7) { // Left center - keep right edge fixed
                    const fixedRightX = originalRectPoints.TopRight.x; // or BottomRight.x (same value)
                    newWidth = Math.abs(fixedRightX - derotatedX);
                    newCenterX = (fixedRightX + derotatedX) / 2;
                    // Height stays the same
                    newHeight = height;
                    newCenterY = originalCenterY;
                }
            }

            // Ensure minimum size
            newWidth = Math.max(20, newWidth);
            newHeight = Math.max(20, newHeight);

            // Calculate new original unrotated points based on the new center and dimensions
            const newOriginalPoints = calculateControlPoints(
                newCenterX,
                newCenterY,
                newWidth,
                newHeight,
                0 // No rotation for original points
            );

            // Update original points
            setOriginalRectPoints(newOriginalPoints);

            // Now calculate the rotated display points
            const updatedRectPoints = calculateControlPoints(
                newCenterX,
                newCenterY,
                newWidth,
                newHeight,
                rectRotation
            );

            // Update display points
            setRectPoints(updatedRectPoints);

            // Update the center point to the new center (this will be the same as newCenterX, newCenterY since it's the center)
            setCenterPoint({ x: newCenterX, y: newCenterY });

            // Update the arrow point to maintain its position relative to the center
            if (imageObj && arrowPoint) {
                const deltaX = newCenterX - centerX;
                const deltaY = newCenterY - centerY;

                const newArrowPoint = {
                    x: arrowPoint.x + deltaX,
                    y: arrowPoint.y + deltaY
                };

                setArrowPoint(newArrowPoint);
                drawImageWithRect(imageObj, updatedRectPoints, newArrowPoint);
            }
        }
    }, [isDraggingPoint, activePointIndex, rectPoints, arrowPoint, imageObj, drawImageWithRect, isRotating, centerPoint, rotatePoint, calculateControlPoints, originalRectPoints, rectRotation, dragOffset]); const handleMouseUp = useCallback(() => {
        setIsDraggingPoint(false);
        setActivePointIndex(null);
        setIsRotating(false);
        setDragOffset({ x: 0, y: 0 }); // Reset drag offset

        // Redraw to remove the rotate icon
        if (imageObj && rectPoints && arrowPoint) {
            drawImageWithRect(imageObj, rectPoints, arrowPoint);
        }
    }, [imageObj, rectPoints, arrowPoint, drawImageWithRect]);

    // Touch event handlers
    const handleTouchStart = useCallback((e) => {
        e.preventDefault();
        const touch = e.touches[0];
        const simulatedEvent = {
            clientX: touch.clientX,
            clientY: touch.clientY,
            button: 0 // Simulate left mouse button
        };
        handleMouseDown(simulatedEvent);
    }, [handleMouseDown]);

    const handleTouchMove = useCallback((e) => {
        e.preventDefault();
        if (!isDraggingPoint && !isRotating) return;

        const touch = e.touches[0];
        const simulatedEvent = {
            clientX: touch.clientX,
            clientY: touch.clientY
        };
        handleMouseMove(simulatedEvent);
    }, [isDraggingPoint, isRotating, handleMouseMove]);

    const handleTouchEnd = useCallback(() => {
        handleMouseUp();
    }, [handleMouseUp]);    // Add and remove event listeners
    useEffect(() => {
        const canvas = canvasRef.current;
        if (canvas && rectPoints) {
            canvas.addEventListener('mousedown', handleMouseDown);
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);

            // Prevent context menu when right-clicking for rotation
            const handleContextMenu = (e) => e.preventDefault();
            canvas.addEventListener('contextmenu', handleContextMenu);

            return () => {
                canvas.removeEventListener('mousedown', handleMouseDown);
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
                canvas.removeEventListener('contextmenu', handleContextMenu);
            };
        }
    }, [rectPoints, handleMouseDown, handleMouseMove, handleMouseUp]);

    // Add wheel event listener for scaling
    useEffect(() => {
        const container = imageContainerRef.current;
        if (container) {
            container.addEventListener('wheel', handleWheel, { passive: false });

            return () => {
                container.removeEventListener('wheel', handleWheel);
            };
        }
    }, [handleWheel]);

    // Add and remove touch event listeners
    useEffect(() => {
        const canvas = canvasRef.current;
        if (canvas && rectPoints) {
            canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
            document.addEventListener('touchmove', handleTouchMove, { passive: false });
            document.addEventListener('touchend', handleTouchEnd);

            return () => {
                canvas.removeEventListener('touchstart', handleTouchStart);
                document.removeEventListener('touchmove', handleTouchMove);
                document.removeEventListener('touchend', handleTouchEnd);
            };
        }
    }, [rectPoints, handleTouchStart, handleTouchMove, handleTouchEnd]);    // Load image and calculate initial scale


    useEffect(() => {
        // Page starts here
        if (!selectedImage || !selectedImage.src) return;

        const image = new Image();
        // Set crossOrigin to handle CORS for AWS S3 images
        // image.crossOrigin = 'anonymous';


        image.src = selectedImage.src;

        if (selectedImage.standardizedImageId)
            setStandardizedImageId(selectedImage.standardizedImageId);

        image.onload = () => {
            // Set the image object and fixed scale
            setImageObj(image);
            setImageLoaded(true);

            // calculate the initial scale based on the container size
            const container = imageContainerRef.current;
            if (!container) return;
            const containerWidth = container.clientWidth;
            const containerHeight = container.clientHeight;

            const imgRatio = image.width / image.height;
            let displayHeight = containerHeight * 0.9; // Use 90% of container height
            let displayWidth = displayHeight * imgRatio;
            if (displayWidth > containerWidth * 0.9) {
                displayWidth = containerWidth * 0.9;
                displayHeight = displayWidth / imgRatio;
            }

            // setScale(0.5); // Fixed scale value
            // prevScaleRef.current = 0.5; // Initialize ref to match

            setScale(displayHeight / image.height); // Set scale based on height
            prevScaleRef.current = displayHeight / image.height; // Initialize ref to match

            setDefaultScaleForReset(displayHeight / image.height); // Set default scale for reset

        };

        image.onerror = () => {
            console.error('Failed to load image with CORS. Trying without crossOrigin...');
            // Try loading without crossOrigin as fallback
            const fallbackImage = new Image();
            fallbackImage.src = selectedImage.src;

            fallbackImage.onload = () => {
                setImageObj(fallbackImage);
                setImageLoaded(true);

                const container = imageContainerRef.current;
                if (!container) return;
                const containerWidth = container.clientWidth;
                const containerHeight = container.clientHeight;

                const imgRatio = fallbackImage.width / fallbackImage.height;
                let displayHeight = containerHeight * 0.9;
                let displayWidth = displayHeight * imgRatio;
                if (displayWidth > containerWidth * 0.9) {
                    displayWidth = containerWidth * 0.9;
                    displayHeight = displayWidth / imgRatio;
                }

                setScale(displayHeight / fallbackImage.height);
                prevScaleRef.current = displayHeight / fallbackImage.height;
                setDefaultScaleForReset(displayHeight / fallbackImage.height);
            };

            fallbackImage.onerror = () => {
                console.error('Failed to load image completely');
                setImageLoaded(false);
            };
        };
    }, [selectedImage]);

    // effect to call handleDetectRect after image is loaded
    useEffect(() => {
        if (rectDetected) return; // Skip if rectangle already detected

        if (imageLoaded && imageObj && !rectDetected) {
            handleDetectRect();
        }
    }, [imageLoaded, imageObj, handleDetectRect]);

    // Effect to redraw canvas when scale or rotation changes
    useEffect(() => {
        if (imageObj) {
            if (rectPoints && arrowPoint) {
                // If rectangle is active, draw image with rectangle
                drawImageWithRect(imageObj, rectPoints, arrowPoint);
            } else {
                // Otherwise just draw the image
                drawImageOnCanvas(imageObj);
            }
        }
    }, [imageObj, drawImageOnCanvas, drawImageWithRect, scale, rotation, rectPoints, arrowPoint]);

    // Effect to update rectangle coordinates when scale changes
    useEffect(() => {
        if (!rectPoints || !originalRectPoints || !centerPoint || !imageObj || !imageContainerRef.current) return;

        // Only update if scale actually changed
        if (Math.abs(prevScaleRef.current - scale) < 0.001) return;

        const scaleRatio = scale / prevScaleRef.current;
        prevScaleRef.current = scale;

        // Get container dimensions
        const container = imageContainerRef.current;
        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;

        // Calculate the center of the canvas (where image is centered)
        const canvasCenter = {
            x: containerWidth / 2,
            y: containerHeight / 2
        };

        // Update all rectangle points by scaling their distance from canvas center
        const updatePointScale = (point) => {
            const offsetX = point.x - canvasCenter.x;
            const offsetY = point.y - canvasCenter.y;
            return {
                x: canvasCenter.x + (offsetX * scaleRatio),
                y: canvasCenter.y + (offsetY * scaleRatio)
            };
        };

        // Update all rectangle points
        const updatedRectPoints = {};
        Object.keys(rectPoints).forEach(key => {
            updatedRectPoints[key] = updatePointScale(rectPoints[key]);
        });

        // Update original rect points (unrotated)
        const updatedOriginalRectPoints = {};
        Object.keys(originalRectPoints).forEach(key => {
            updatedOriginalRectPoints[key] = updatePointScale(originalRectPoints[key]);
        });

        // Update center point
        const updatedCenterPoint = updatePointScale(centerPoint);

        // Update arrow point
        const updatedArrowPoint = arrowPoint ? updatePointScale(arrowPoint) : null;

        // Set the updated points
        setRectPoints(updatedRectPoints);
        setOriginalRectPoints(updatedOriginalRectPoints);
        setCenterPoint(updatedCenterPoint);
        if (updatedArrowPoint) {
            setArrowPoint(updatedArrowPoint);
        }

        console.log('Rectangle coordinates updated for scale change:', scale);

        // Use eslint-disable to prevent warnings about missing dependencies that would cause infinite loops
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scale, rectRotation]); // Only depend on scale and rectRotation to avoid infinite loops    // Function to get standardized image data from the current rectangle
    // eslint-disable-next-line no-unused-vars
    const getStandardizedData = useCallback(() => {
        if (!rectPoints || !arrowPoint || !imageObj || !centerPoint || !originalRectPoints) return null;

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');// Get the rectangle dimensions using the unrotated points for a perfect rectangle
        const width = Math.abs(originalRectPoints.TopRight.x - originalRectPoints.TopLeft.x);
        const height = Math.abs(originalRectPoints.BottomLeft.y - originalRectPoints.TopLeft.y);

        canvas.width = width;
        canvas.height = height;

        // Draw the image on the canvas rotated to account for rectangle rotation
        ctx.save();
        ctx.translate(width / 2, height / 2);        // Apply the rectangle's rotation to straighten the cropped image
        ctx.rotate((rectRotation * Math.PI) / 180);

        // Instead of drawing from the source canvas, we need to work with the original image
        // and calculate the proper transformation
        const container = imageContainerRef.current;
        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;

        // Calculate how the original image is displayed on the canvas
        const imgRatio = imageObj.width / imageObj.height;
        let displayHeight = containerHeight * 0.9;
        let displayWidth = displayHeight * imgRatio;
        displayWidth *= scale;
        displayHeight *= scale;

        // Calculate the mapping from canvas coordinates to image coordinates
        const canvasToImageScaleX = imageObj.width / displayWidth;
        const canvasToImageScaleY = imageObj.height / displayHeight;

        // Calculate the image center on canvas
        const imageCenterX = containerWidth / 2;
        const imageCenterY = containerHeight / 2;

        // Convert rectangle coordinates to image coordinates
        const rectCenterImageX = (centerPoint.x - imageCenterX) * canvasToImageScaleX + imageObj.width / 2;
        const rectCenterImageY = (centerPoint.y - imageCenterY) * canvasToImageScaleY + imageObj.height / 2;
        const rectWidthImage = width * canvasToImageScaleX;
        const rectHeightImage = height * canvasToImageScaleY;

        // Draw the original image portion that corresponds to the rectangle
        const sourceX = rectCenterImageX - rectWidthImage / 2;
        const sourceY = rectCenterImageY - rectHeightImage / 2;

        ctx.drawImage(
            imageObj,
            sourceX, sourceY, rectWidthImage, rectHeightImage,
            -width / 2, -height / 2, width, height
        );

        ctx.restore();

        // Get the image data
        const standardizedImage = getCanvasBase64Safe(canvas);

        // Return both the image and the rectangle data
        return {
            base64: standardizedImage,
            croppedImage: canvas, rect: {
                TopLeft: rectPoints.TopLeft,
                TopRight: rectPoints.TopRight,
                BottomRight: rectPoints.BottomRight,
                BottomLeft: rectPoints.BottomLeft,
                rotation: rectRotation
            }
        };
    }, [rectPoints, arrowPoint, imageObj, originalRectPoints, centerPoint, rectRotation, scale, getCanvasBase64Safe]);

    // Render preview whenever showPreview changes (fallback only)
    useEffect(() => {
        // This useEffect is now only used as a fallback when API calls fail
        // The main preview functionality is handled in handlePreview function
    }, [showPreview]);

    // Handle redrawing when exiting preview mode
    useEffect(() => {
        // Only redraw when exiting preview mode (showPreview becomes false)
        // and we have the necessary data for drawing the rectangle
        if (!showPreview && rectPoints && arrowPoint && imageObj && rectDetected) {
            // Use setTimeout to avoid potential render loop issues
            const timeoutId = setTimeout(() => {
                const canvas = canvasRef.current;
                if (!canvas || !imageContainerRef.current) return;

                const container = imageContainerRef.current;
                const containerWidth = container.clientWidth;
                const containerHeight = container.clientHeight;

                // Set canvas dimensions
                canvas.width = containerWidth;
                canvas.height = containerHeight;

                const ctx = canvas.getContext('2d');
                ctx.clearRect(0, 0, canvas.width, canvas.height);

                // Save the current context state
                ctx.save();

                // Move to the center for rotation
                ctx.translate(canvas.width / 2, canvas.height / 2);

                // Rotate around this point
                ctx.rotate((rotation * Math.PI) / 180);

                // Apply scale transformation
                ctx.scale(scale, scale);

                // Draw the image centered using original image dimensions
                ctx.drawImage(
                    imageObj,
                    -imageObj.width / 2,   // Center horizontally
                    -imageObj.height / 2,  // Center vertically
                    imageObj.width,        // Use original image width
                    imageObj.height        // Use original image height
                );

                // Restore the context state
                ctx.restore();

                // Now draw the rectangle and control points
                // Calculate center point from the rectPoints
                const centerX = (rectPoints.TopLeft.x + rectPoints.BottomRight.x) / 2;
                const centerY = (rectPoints.TopLeft.y + rectPoints.BottomRight.y) / 2;

                // Draw the rectangle connecting the points
                ctx.beginPath();
                ctx.moveTo(rectPoints.TopLeft.x, rectPoints.TopLeft.y);
                ctx.lineTo(rectPoints.TopRight.x, rectPoints.TopRight.y);
                ctx.lineTo(rectPoints.BottomRight.x, rectPoints.BottomRight.y);
                ctx.lineTo(rectPoints.BottomLeft.x, rectPoints.BottomLeft.y);
                ctx.closePath();
                ctx.strokeStyle = colors.blue;
                ctx.lineWidth = 2;
                ctx.setLineDash([]); // Solid line
                ctx.stroke();

                // Draw center point of the rectangle
                ctx.beginPath();
                ctx.arc(centerX, centerY, 10, 0, 2 * Math.PI);
                ctx.fillStyle = colors.yellow;
                ctx.fill();
                // ctx.strokeStyle = colors.blue;
                // ctx.stroke();

                // Draw arrow
                const arrowLength = 60;
                const angleRad = (rectRotation * Math.PI) / 180;
                const dirX = Math.sin(angleRad);
                const dirY = -Math.cos(angleRad);
                const arrowEndX = centerX + dirX * arrowLength;
                const arrowEndY = centerY + dirY * arrowLength;

                ctx.beginPath();
                ctx.moveTo(centerX, centerY);
                ctx.lineTo(arrowEndX, arrowEndY);
                ctx.strokeStyle = colors.red;
                ctx.lineWidth = 2;
                ctx.stroke();

                // Draw arrowhead
                const headLength = 15;
                const angle = Math.atan2(dirY, dirX);
                ctx.beginPath();
                ctx.moveTo(arrowEndX, arrowEndY);
                ctx.lineTo(
                    arrowEndX - headLength * Math.cos(angle - Math.PI / 6),
                    arrowEndY - headLength * Math.sin(angle - Math.PI / 6)
                );
                ctx.moveTo(arrowEndX, arrowEndY);
                ctx.lineTo(
                    arrowEndX - headLength * Math.cos(angle + Math.PI / 6),
                    arrowEndY - headLength * Math.sin(angle + Math.PI / 6)
                );
                ctx.stroke();

                // Draw control points
                const pointKeys = ['TopLeft', 'CenterTop', 'TopRight', 'CenterRight', 'BottomRight', 'CenterBottom', 'BottomLeft', 'CenterLeft'];
                pointKeys.forEach((key, index) => {
                    const point = rectPoints[key];
                    ctx.beginPath();
                    ctx.arc(point.x, point.y, 8, 0, 2 * Math.PI);
                    ctx.fillStyle = colors.blue;
                    ctx.fill();
                    ctx.strokeStyle = colors.red;
                    ctx.stroke();
                });
            }, 50); // Small delay to ensure state has settled

            return () => clearTimeout(timeoutId);
        }
    }, [showPreview, rectPoints, arrowPoint, imageObj, rectDetected, rotation, scale, rectRotation, colors]);

    // Update the detect rectangle handler
    const handleDetectAndStandardize = () => {
        handleDetectRect();
    };    // Handle increase rotation button click
    const handleIncreaseRotation = () => {
        if (!rectPoints || !originalRectPoints || !centerPoint) return;

        let newRotation = rectRotation + 1; // Increase by 1 degrees
        if (newRotation > 360) newRotation = newRotation - 360; // Wrap around after 360
        setRectRotation(newRotation);

        // Recalculate rotated points
        const rotatedPoints = calculateControlPoints(
            centerPoint.x,
            centerPoint.y,
            Math.abs(originalRectPoints.TopRight.x - originalRectPoints.TopLeft.x),
            Math.abs(originalRectPoints.BottomLeft.y - originalRectPoints.TopLeft.y),
            newRotation
        );
        setRectPoints(rotatedPoints);

        // Redraw the rectangle
        if (imageObj && arrowPoint) {
            drawImageWithRect(imageObj, rotatedPoints, arrowPoint);
        }

        //console.log("Rotation increased to:", newRotation);
    };

    // Handle decrease rotation button click
    const handleDecreaseRotation = () => {
        if (!rectPoints || !originalRectPoints || !centerPoint) return;

        let newRotation = rectRotation - 1; // Decrease by 1 degrees
        if (newRotation < -359) newRotation = newRotation + 360; // Wrap around after -359
        setRectRotation(newRotation);

        // Recalculate rotated points
        const rotatedPoints = calculateControlPoints(
            centerPoint.x,
            centerPoint.y,
            Math.abs(originalRectPoints.TopRight.x - originalRectPoints.TopLeft.x),
            Math.abs(originalRectPoints.BottomLeft.y - originalRectPoints.TopLeft.y),
            newRotation
        );
        setRectPoints(rotatedPoints);

        // Redraw the rectangle
        if (imageObj && arrowPoint) {
            drawImageWithRect(imageObj, rotatedPoints, arrowPoint);
        }

        //console.log("Rotation decreased to:", newRotation);
    };

    const getPercentScaleToShow = (scale) => {
        return (Math.round(scale * 1000) / 10).toFixed(2); // Convert scale to percentage with 2 decimal places
    };

    return (
        <div className="image-editor-overlay">
            <div className="image-editor-modal">
                <div className="editor-header">
                    <h3>Manual Standardize</h3>
                    <div className="header-buttons">
                        <button className="control-btn close-btn" onClick={onClose} title="Close">
                            <i className="fa fa-times"></i>
                        </button>
                    </div>
                </div>

                <div className="controls-top">
                    <div className="zoom-controls">
                        <button
                            style={{ display: 'none' }}
                            className="control-btn zoom-btn"
                            onClick={() => {
                                //console.log('Decrease button clicked, current scale:', scale);
                                handleDecreaseScale();
                            }}
                            title="Zoom Out"
                        >
                            <i className="fa fa-search-minus"></i>
                        </button>
                        <div className="zoom-slider-container">
                            <button
                                className="control-btn ratio-btn"
                                onClick={handleDecreaseScale}
                                title="Decrease Scale (-0.01)"
                            >
                                <i className="fa fa-minus"></i>
                            </button>

                            <div className="ratio-info">
                                <label className="ratio-label">Scale:</label>
                                <input
                                    type="number"
                                    min="0.1"
                                    max="2.0"
                                    step="0.01"
                                    value={getPercentScaleToShow(scale)}
                                    onChange={handleScaleRatioChange}
                                    className="ratio-input"
                                    title="Image scale value"
                                />
                                <span className="ratio-unit">%</span>
                            </div>

                            <button
                                className="control-btn ratio-btn"
                                onClick={handleIncreaseScale}
                                title="Increase Scale (+0.01)"
                            >
                                <i className="fa fa-plus"></i>
                            </button>
                        </div>
                        {rectDetected && (
                            <div className="rotation-display-container">
                                <button
                                    className="control-btn rotate-btn"
                                    onClick={() => {
                                        //console.log('Decrease rotation clicked, current angle:', rectRotation);
                                        handleDecreaseRotation();
                                    }}
                                    title="Rotate Left (-1°)"
                                >
                                    <i className="fa fa-minus"></i>
                                </button>

                                <div className="rotation-info">
                                    <label className="rotation-label">Rotation:</label>
                                    <div className="rotation-value">
                                        {Math.round(rectRotation)}°
                                    </div>
                                </div>

                                <button
                                    className="control-btn rotate-btn"
                                    onClick={() => {
                                        // console.log('Increase rotation clicked, current angle:', rectRotation);
                                        handleIncreaseRotation();
                                    }}
                                    title="Rotate Right (+1°)"
                                >
                                    <i className="fa fa-plus"></i>
                                </button>
                            </div>
                        )}

                        <button
                            style={{ display: 'none' }}
                            className="control-btn zoom-btn"
                            onClick={() => {
                                // console.log('Increase button clicked, current scale:', scale);
                                handleIncreaseScale();
                            }}
                            title="Zoom In"
                        >
                            <i className="fa fa-search-plus"></i>
                        </button>
                    </div>

                    <div className="rotate-controls">
                        {/* <button className="control-btn reset-btn" onClick={handleReset} title="Reset View">
                            <i className="fa fa-refresh"></i>
                        </button> */}
                        <button className="control-btn color-btn" onClick={handleOpenColorPicker} title="Customize Colors">
                            <i className="fa fa-cog"></i>
                        </button>
                    </div>
                </div>

                <div className="image-container" ref={imageContainerRef}>
                    {showPreview ? (
                        <canvas
                            ref={previewCanvasRef}
                            className={`canvas-editor ${imageLoaded ? 'loaded' : ''}`}
                        />
                    ) : (
                        <canvas
                            ref={canvasRef}
                            className={`canvas-editor ${imageLoaded ? 'loaded' : ''}`}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onContextMenu={(e) => e.preventDefault()}
                        />
                    )}
                    {!imageLoaded && (
                        <div className="loading-spinner">
                            <i className="fa fa-spinner fa-spin"></i>
                        </div>
                    )}
                    {isProcessingPreview && (
                        <div className="processing-overlay">
                            <div className="processing-spinner">
                                <i className="fa fa-spinner fa-spin"></i>
                                <span>Processing...</span>
                            </div>
                        </div>
                    )}
                </div>

                <div className="controls-bottom">
                    {rectDetected ? (<>
                        <button
                            className="btn preview-btn"
                            onClick={handlePreview}
                            disabled={isProcessingPreview || showPreview}
                        >
                            {isProcessingPreview ? (
                                <>
                                    <i className="fa fa-spinner fa-spin"></i> Processing...
                                </>
                            ) : (
                                <>
                                    <i className="fa fa-eye"></i> Preview
                                </>
                            )}
                        </button>
                        {!isProcessingPreview && showPreview && (
                            <button
                                className="btn close-btn"
                                onClick={handleEditCurrentStandardization}
                            >
                                <i className="fa fa-pencil"></i> Edit
                            </button>
                        )}
                        <button
                            className="btn detect-btn"
                            onClick={handleConfirm}
                            disabled={!previewCompleted}
                        >
                            <i className="fa fa-check"></i> Confirm
                        </button>

                        {/* <button className="btn close-btn" onClick={handleCancel}>
                            <i className="fa fa-refresh"></i> Reset
                        </button>

                        <button className="btn close-btn" onClick={onClose}>
                            <i className="fa fa-times"></i> Close
                        </button> */}
                    </>
                    ) : (
                        <>
                            <button className="btn detect-btn" onClick={handleDetectAndStandardize}>
                                <i className="fa fa-crop"></i> Detect Rectangle
                            </button>
                            <button className="btn close-btn" onClick={onClose}>
                                <i className="fa fa-times"></i> Close
                            </button>
                        </>
                    )}
                </div>

                <div className="editor-instructions">
                    <div className="instruction-text">
                        {!rectDetected ? (
                            <p><i className="fa fa-info-circle"></i> Click "Detect Rectangle" to automatically detect the shoe sole, or manually draw a rectangle on the image.</p>
                        ) : (
                            <p><i className="fa fa-hand-pointer-o"></i> Drag the corner points to adjust the rectangle. Right-click and drag to rotate.</p>
                        )}
                    </div>
                </div>
            </div>

            {showColorPicker && (
                <ColorPicker
                    colors={colors}
                    onColorsChange={handleColorsChange}
                    onClose={handleCloseColorPicker}
                />
            )}
        </div>
    );
};

export default ImageEditor;